<template>
    <div class="timer-product-portrait-filters">
        <PortraitSelectDropdown
            :options="categoryOptions"
            :value="selectedCategoryId"
            :open="openDropdown === 'category'"
            icon-class="i-andy-quantity-stack"
            :aria-label="$t('timersproduct.filters.categories')"
            @update:open="onDropdownOpen('category', $event)"
            @select="onSelectCategory"
        />

        <PortraitSelectDropdown
            :options="statusOptions"
            :value="selectedStatusId"
            :open="openDropdown === 'status'"
            icon-class="i-andy-quantity-stack"
            :aria-label="$t('timersproduct.filters.statuses')"
            @update:open="onDropdownOpen('status', $event)"
            @select="onSelectStatus"
        />

        <PortraitSelectDropdown
            v-if="showCollectionFilter"
            :options="collectionOptions"
            :value="selectedCollectionId"
            :open="openDropdown === 'collection'"
            icon-class="i-andy-quantity-stack"
            :aria-label="$t('timersproduct.filters.collections')"
            @update:open="onDropdownOpen('collection', $event)"
            @select="onSelectCollection"
        />
    </div>
</template>

<script>
import PortraitSelectDropdown from '@/components/domain/timerproduct/PortraitSelectDropdown.vue'

/**
 * Sentinel de “todos los estados”. No usar `false`: el store reserva ese id
 * para el estado sintético caducado / to_delete, que no es un filtro de producto.
 */
export const PORTRAIT_STATUS_ALL = 'all'

/**
 * BUILD-7643: el filtro de colecciones en portrait queda desactivado de
 * forma reversible. Poner a `true` para volver a mostrarlo.
 */
export const SHOW_PORTRAIT_COLLECTION_FILTER = false

/**
 * Filtros del listado de productos en portrait (BUILD-7555).
 * Desktop / landscape conservan el diseño anterior de categorías.
 */
export default {
    name: 'TimerProductPortraitFilters',
    components: {
        PortraitSelectDropdown
    },
    props: {
        categories: {
            type: Array,
            default() {
                return []
            }
        },
        statuses: {
            type: Array,
            default() {
                return []
            }
        },
        collections: {
            type: Array,
            default() {
                return []
            }
        },
        selectedCategoryId: {
            type: [String, Number, Boolean],
            default: false
        },
        selectedStatusId: {
            type: [String, Number, Boolean],
            default: false
        },
        selectedCollectionId: {
            type: [String, Number],
            default: -1
        }
    },
    data() {
        return {
            openDropdown: null
        }
    },
    computed: {
        hasCollections() {
            return Array.isArray(this.collections) && this.collections.length > 0
        },
        /**
         * Props, opciones y handler de colecciones se conservan para reactivar
         * el dropdown sin reescribir el cableado (BUILD-7643).
         */
        showCollectionFilter() {
            return SHOW_PORTRAIT_COLLECTION_FILTER && this.hasCollections
        },
        categoryOptions() {
            return [
                {
                    id: false,
                    label: this.$t('timersproduct.filters.all_categories'),
                    selected: this.selectedCategoryId === false || this.selectedCategoryId === null
                },
                ...this.categories
                    .filter((category) => category && category.id != null)
                    .map((category) => ({
                        id: category.id,
                        label: category.name,
                        // No usar category.qty: cuenta timers activos, no productos del listado.
                        selected: category.id === this.selectedCategoryId
                    }))
            ]
        },
        statusOptions() {
            const allSelected = this.selectedStatusId === false || this.selectedStatusId === null || this.selectedStatusId === '' || this.selectedStatusId === PORTRAIT_STATUS_ALL

            return [
                {
                    id: PORTRAIT_STATUS_ALL,
                    label: this.$t('timersproduct.filters.all_statuses'),
                    selected: allSelected
                },
                ...this.statuses
                    .filter((status) => status && status.id !== false && status.id != null)
                    .map((status) => ({
                        id: status.id,
                        label: status.name,
                        selected: status.id === this.selectedStatusId
                    }))
            ]
        },
        collectionOptions() {
            return [
                {
                    id: -1,
                    label: this.$t('timersproduct.filters.all_collections'),
                    selected: this.selectedCollectionId === -1 || this.selectedCollectionId === false
                },
                ...this.collections.map((collection) => ({
                    id: collection.id,
                    label: collection.name,
                    selected: collection.id === this.selectedCollectionId
                }))
            ]
        }
    },
    methods: {
        onDropdownOpen(name, isOpen) {
            this.openDropdown = isOpen ? name : this.openDropdown === name ? null : this.openDropdown
        },
        onSelectCategory(option) {
            this.$emit('select-category', option.id)
        },
        onSelectStatus(option) {
            this.$emit('select-status', option.id)
        },
        onSelectCollection(option) {
            this.$emit('select-collection', option.id)
        }
    }
}
</script>

<style lang="scss" scoped>
.timer-product-portrait-filters {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    padding: 8px 0 4px;
    box-sizing: border-box;
    flex-shrink: 0;
    position: relative;
    z-index: $z-menu-top;
}
</style>
